import { useState } from "react";
import { GOLD, NAVY } from "../styles/palette";
import { getPerVisit } from "../lib/pricing";
import { ClickableDate } from "./ClickableDate";
import { useIsMobile } from "../lib/useIsMobile";

// Build the initial line-items list for the editor.
// Priority:
//   1. invoice.line_items (Stripe-mirrored, the new source of truth)
//   2. Legacy fallback: one synthetic "Dog Waste Removal" line +
//      credits parsed from invoice.credits JSON
// Each item carries `invoice_item` (the Stripe ii_xxx id) so the save
// path can update the exact Stripe item instead of guessing.
function buildInitialLineItems(invoice) {
  if (Array.isArray(invoice.line_items) && invoice.line_items.length > 0) {
    return invoice.line_items.map(li => ({
      invoice_item: li.invoice_item || null,
      description:  li.description || "",
      amount:       Number(li.amount),
      isCredit:     Number(li.amount) < 0,
    }));
  }
  // Legacy fallback for invoices that pre-date the line-item mirror
  const savedCredits = (() => { try { return JSON.parse(invoice.credits||"[]"); } catch(e) { return []; } })();
  const baseAmount   = Number(invoice.amount) + savedCredits.reduce((s,c)=>s+Number(c.amount),0);
  const label        = invoice.frequency === "weekly"        ? "Weekly Dog Waste Removal"
                     : invoice.frequency === "biweekly"      ? "Bi-Weekly Dog Waste Removal"
                     : invoice.frequency === "twice-weekly"  ? "Twice-Weekly Dog Waste Removal"
                     : "Dog Waste Removal";
  return [
    { invoice_item: null, description: label, amount: baseAmount, isCredit: false },
    ...savedCredits.map(c => ({ invoice_item: null, description:`Credit — skipped visit ${c.date}`, amount:-Number(c.amount), isCredit:true })),
  ];
}

export function InvoiceEditModal({ invoice, onSave, onSaveAndPreview, onClose, onApprove }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    client_name:    invoice.client_name    ?? "",
    invoice_number: invoice.invoice_number ?? "",
    date:           invoice.date           ?? "",
    due_date:       invoice.due_date       ?? "",
    frequency:      invoice.frequency      ?? "",
    dogs:           invoice.dogs           ?? 1,
    status:         invoice.status         ?? "open",
    notes:          invoice.notes          ?? "",
  });
  const [lineItems, setLineItems] = useState(() => buildInitialLineItems(invoice));
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));
  const total = lineItems.reduce((s,i)=>s+Number(i.amount),0);

  function updateLine(idx, field, val) {
    setLineItems(items=>items.map((item,i)=>{
      if (i !== idx) return item;
      const next = { ...item, [field]: field==="amount" ? Number(val) : val };
      // Keep isCredit in sync with the sign so save logic stays correct
      if (field === "amount") next.isCredit = Number(val) < 0;
      return next;
    }));
  }

  // Save:
  //   - line_items array goes through to update-invoice for per-Stripe-item
  //     editing (cleanup updates cleanup, deo updates deo, new lines get
  //     created, removed lines get deleted in Stripe)
  //   - credits JSON kept for backward-compat with the email template
  function handleSaveClick() {
    onSave({
      ...invoice,
      ...form,
      amount: total,
      line_items: lineItems
        .filter(i => Number(i.amount) !== 0)
        .map(i => ({
          invoice_item: i.invoice_item || null,
          description:  i.description,
          amount:       Number(i.amount),
        })),
      credits: JSON.stringify(
        lineItems
          .filter(i => i.isCredit)
          .map(i => ({ date: i.description.replace("Credit — skipped visit ",""), amount: Math.abs(i.amount), reason: "Manual credit" }))
      ),
    });
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:580}}>
        <div className="modal-header">
          <h3>✏️ Edit Invoice — {invoice.invoice_number}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Basic fields */}
          <div className="form-grid" style={{marginBottom:16}}>
            <div className="form-group"><label>Client Name</label><input value={form.client_name} onChange={f("client_name")}/></div>
            <div className="form-group">
              <label>Invoice #</label>
              <div style={{padding:"8px 10px",background:"#f5f6f8",border:"1px solid #e3e8ee",borderRadius:6,fontSize:13,color:"#1a1f36",fontWeight:600}}>{invoice.invoice_number || "—"}</div>
              <div style={{fontSize:11,color:"#8993a4",marginTop:4}}>Set by Stripe</div>
            </div>
            <div className="form-group"><label>Invoice Date</label><ClickableDate value={form.date} onChange={v=>setForm(p=>({...p,date:v}))}/></div>
            <div className="form-group"><label>Due Date</label><ClickableDate value={form.due_date} onChange={v=>setForm(p=>({...p,due_date:v}))}/></div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={f("status")}>
                <option value="draft">Draft</option>
                <option value="open">Open</option>
                <option value="paid">Paid</option>
                <option value="uncollectible">Uncollectible</option>
              </select>
            </div>
            <div className="form-group"><label>Notes</label><input value={form.notes} onChange={f("notes")}/></div>
          </div>

          {/* Line items */}
          <div style={{fontWeight:600,fontSize:13,color:"#1a1f36",marginBottom:8}}>Line Items</div>
          {lineItems.map((item,idx)=>(
            <div key={idx} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,background:item.isCredit?"#fff8f8":"#f5f6f8",borderRadius:6,padding:"8px 10px",border:item.isCredit?"1px solid #ffbdad":"none"}}>
              {/* minWidth:0 lets the description shrink below its ~170px intrinsic width
                  so description + $amount + × fit a 375px sheet without clipping. */}
              <input value={item.description} onChange={e=>updateLine(idx,"description",e.target.value)} style={{flex:1,minWidth:0,fontSize:12}}/>
              <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                <span style={{fontSize:12,color:"#8993a4"}}>$</span>
                <input type="number" step="0.01" value={item.amount} onChange={e=>updateLine(idx,"amount",e.target.value)} style={{width:isMobile?72:80,minWidth:0,fontSize:12,color:item.isCredit?"#bf2600":NAVY,fontWeight:600}}/>
              </div>
              <button aria-label="Remove line" onClick={()=>setLineItems(items=>items.filter((_,i)=>i!==idx))} style={isMobile
                ? {background:"none",border:"none",cursor:"pointer",color:"#bf2600",fontSize:20,flexShrink:0,width:36,height:36,display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0,marginRight:-6}
                : {background:"none",border:"none",cursor:"pointer",color:"#bf2600",fontSize:16,flexShrink:0}}>×</button>
            </div>
          ))}
          <button className="btn btn-ghost btn-sm" onClick={()=>setLineItems(items=>[...items,{description:"",amount:0,isCredit:false}])} style={{marginBottom:12}}>+ Add Line</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>setLineItems(items=>[...items,{description:"Credit — skipped visit",amount:-getPerVisit(invoice.frequency||"weekly",invoice.dogs||1),isCredit:true}])} style={{marginBottom:12,marginLeft:8,color:"#bf2600"}}>+ Add Credit</button>

          <div style={{display:"flex",justifyContent:"space-between",background:"#f0f4ff",borderRadius:6,padding:"10px 14px",fontWeight:700,fontSize:15,color:NAVY}}>
            <span>Total</span><span>${total.toFixed(2)}</span>
          </div>

          {invoice.status==="draft" && (
            <div style={{background:"#fffbeb",border:"1px solid "+GOLD,borderRadius:8,padding:"10px 14px",marginTop:12,fontSize:12,color:"#854d0e"}}>
              💳 After editing, click <strong>Finalize & Charge</strong> to charge ${total.toFixed(2)} to the client's card on file.
            </div>
          )}
        </div>
        {/* Phone: four nowrap buttons (~536px) can't sit in one 375px row — Cancel +
            Save & Preview share the first row, Save / Finalize each get a full row. */}
        <div className="modal-footer" style={isMobile?{flexWrap:"wrap"}:undefined}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {onSaveAndPreview&&<button className="btn btn-ghost" style={isMobile?{flex:"1 1 auto",justifyContent:"center"}:undefined} onClick={()=>onSaveAndPreview({...invoice,...form, amount:total, credits:JSON.stringify(lineItems.filter(i=>i.isCredit).map(i=>({date:i.description.replace("Credit — skipped visit ",""),amount:Math.abs(i.amount),reason:"Manual credit"})))})}>💾 Save & Preview</button>}
          <button className="btn btn-primary" style={isMobile?{flex:"1 1 100%",justifyContent:"center"}:undefined} onClick={handleSaveClick}>Save Changes</button>
          {invoice.status==="draft" && (
            <button className="btn btn-gold" style={isMobile?{flex:"1 1 100%",justifyContent:"center"}:undefined} onClick={()=>onApprove({...invoice,...form,amount:Number(form.amount)})}>⚡ Finalize & Charge</button>
          )}
        </div>
      </div>
    </div>
  );
}

